<template>
  <div class="schedule-board">
    <div class="container">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h2 class="mb-0">
          <i class="fa fa-calendar mr-2"></i>
          스케줄
        </h2>
        <div class="d-flex align-items-center">
          <span class="text-muted mr-3">
            <i class="fa fa-refresh mr-1" :class="{ 'fa-spin': isLoading }"></i>
            {{ lastUpdated }}
          </span>
          <button
            class="btn btn-outline-primary btn-sm"
            @click="fetchSchedules"
            :disabled="isLoading"
          >
            <i class="fa fa-refresh mr-1"></i>
            새로고침
          </button>
        </div>
      </div>

      <!-- 로딩 상태 -->
      <div v-if="isLoading && schedules.length === 0" class="text-center py-5">
        <div class="spinner-border text-primary mb-3" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="text-muted">스케줄을 불러오는 중...</p>
      </div>

      <!-- 에러 상태 -->
      <div v-else-if="error" class="alert alert-danger mb-4" role="alert">
        <i class="fa fa-exclamation-triangle mr-2"></i>
        {{ error }}
        <button class="btn btn-outline-danger btn-sm ml-2" @click="fetchSchedules">
          다시 시도
        </button>
      </div>

      <!-- 스케줄 테이블 -->
      <div v-else-if="schedules.length > 0" class="schedule-table-container">
        <div class="table-responsive schedule-table">
          <table class="table table-striped table-hover">
            <thead>
              <tr>
                <th class="text-center">날짜</th>
                <th class="text-center">시간</th>
                <th class="text-center">제목</th>
                <th class="text-center">설명</th>
                <th class="text-center">담당자</th>
                <th class="text-center">장소</th>
                <th class="text-center">상태</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in schedules" :key="idx" :class="getRowClass(item, 'row')">
                <td class="text-center">
                  <i class="fa fa-calendar mr-1"></i>
                  {{ formatDate(item.date) }}
                </td>
                <td class="text-center">
                  <i class="fa fa-clock-o mr-1"></i>
                  {{ item.time || '-' }}
                </td>
                <td class="text-center">
                  <strong>{{ item.title }}</strong>
                </td>
                <td class="text-center">
                  {{ item.description || '-' }}
                </td>
                <td class="text-center">
                  <span v-if="item.manager">
                    <i class="fa fa-user mr-1"></i>
                    {{ item.manager }}
                  </span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td class="text-center">
                  <span v-if="item.location">
                    <i class="fa fa-map-marker mr-1"></i>
                    {{ item.location }}
                  </span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td class="text-center">
                  <span v-if="item.status">
                    <span :class="'badge badge-' + getStatusVariant(item.status)">{{ item.status }}</span>
                  </span>
                  <span v-else class="text-muted">-</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- 자동 새로고침 안내 -->
        <div class="text-center mt-3">
          <small class="text-muted">
            <i class="fa fa-info-circle mr-1"></i>
            스케줄은 60초마다 자동으로 업데이트됩니다.
          </small>
        </div>
      </div>

      <!-- 빈 상태 -->
      <div v-else class="text-center py-5">
        <i class="fa fa-calendar-times fa-3x text-muted mb-3"></i>
        <p class="text-muted">등록된 스케줄이 없습니다.</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/plugins/axios'
import moment from 'moment'

export default {
  name: 'ScheduleBoard',
  data() {
    return {
      schedules: [],
      isLoading: false,
      error: null,
      lastUpdated: null,
      refreshInterval: null,
      // fields 제거: 직접 테이블 렌더링 방식으로 변경
    }
  },
  mounted() {
    this.fetchSchedules()
    this.startAutoRefresh()
  },
  beforeDestroy() {
    this.stopAutoRefresh()
  },
  methods: {
    async fetchSchedules() {
      this.isLoading = true
      this.error = null

      try {
        const response = await axios.get(process.env.VUE_APP_PUBLIC_API_URL +'/schedules')
        
        if (response.data.success) {
          this.schedules = response.data.data || []
          this.lastUpdated = moment().format('YYYY-MM-DD HH:mm:ss')
        } else {
          throw new Error(response.data.error || '알 수 없는 오류가 발생했습니다.')
        }
      } catch (error) {
        console.error('스케줄 조회 실패:', error)
        this.error = (error.response && error.response.data && error.response.data.error) || '스케줄을 불러오는데 실패했습니다.'
        this.schedules = []
      } finally {
        this.isLoading = false
      }
    },
    
    startAutoRefresh() {
      // 60초마다 자동 새로고침
      this.refreshInterval = setInterval(() => {
        this.fetchSchedules()
      }, 60000)
    },
    
    stopAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval)
        this.refreshInterval = null
      }
    },
    
    formatDate(dateStr) {
      if (!dateStr) return '-'
      return moment(dateStr).format('MM/DD (ddd)')
    },
    
    getStatusVariant(status) {
      if (!status) return 'secondary'
      
      const statusLower = status.toLowerCase()
      if (['예정', 'scheduled'].includes(statusLower)) return 'primary'
      if (['진행중', 'ongoing', 'active'].includes(statusLower)) return 'success'
      if (['완료', 'completed', 'done'].includes(statusLower)) return 'secondary'
      if (['취소', 'cancelled', 'canceled'].includes(statusLower)) return 'danger'
      if (['연기', 'postponed', 'delayed'].includes(statusLower)) return 'warning'
      
      return 'info'
    },
    
    getRowClass(item, type) {
      if (type !== 'row') return
      
      const status = item.status && item.status.toLowerCase()
      if (['취소', 'cancelled', 'canceled'].includes(status)) {
        return 'table-danger-light'
      }
      if (['완료', 'completed', 'done'].includes(status)) {
        return 'table-secondary-light'
      }
      if (['진행중', 'ongoing', 'active'].includes(status)) {
        return 'table-success-light'
      }
      
      return ''
    }
  }
}
</script>

<style scoped>
.schedule-board {
  padding: 20px 0;
}

.container {
  max-width: 1140px;
  margin: 0 auto;
  padding: 0 15px;
}

h2 {
  color: #27ae60;
  margin-bottom: 20px;
  font-weight: 600;
  padding-bottom: 10px;
  border-bottom: 2px solid #27ae60;
}

h2 i {
  color: #27ae60;
  margin-right: 8px;
}

.schedule-table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
}

.schedule-table {
  margin-bottom: 0;
}

.table thead th {
  background-color: #27ae60;
  color: white;
  font-weight: 600;
  padding: 15px;
  border: none;
}

.table tbody td {
  padding: 15px;
  border-bottom: 1px solid #f1f3f4;
  vertical-align: middle;
}

.table tbody tr:hover {
  background-color: rgba(39, 174, 96, 0.05);
}

.table tbody tr:last-child td {
  border-bottom: none;
}

.btn-outline-primary {
  color: #27ae60;
  border-color: #27ae60;
}

.btn-outline-primary:hover {
  background-color: #27ae60;
  border-color: #27ae60;
}

.title-cell {
  font-weight: 600;
  color: #2c3e50;
}

/* 행 상태별 배경색 */
.table-danger-light {
  background-color: rgba(220, 53, 69, 0.1) !important;
}

.table-secondary-light {
  background-color: rgba(108, 117, 125, 0.1) !important;
}

.table-success-light {
  background-color: rgba(39, 174, 96, 0.1) !important;
}

/* 반응형 테이블 */
@media (max-width: 768px) {
  .schedule-board {
    padding: 15px 0;
  }
  
  .schedule-table-container {
    font-size: 0.9rem;
  }
}

/* 스크롤바 스타일링 */
.schedule-table-container::-webkit-scrollbar {
  height: 8px;
}

.schedule-table-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.schedule-table-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.schedule-table-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
